import React, { useEffect, useRef } from 'react';

export interface Conversation {
  id: string;
  title: string;
  lastMessage?: string;
  timestamp?: Date;
}

interface ChatSidebarProps {
  conversations: Conversation[];
  currentConversationId?: string;
  collapsed: boolean;
  hasMoreConversations: boolean;
  isLoadingMoreConversations: boolean;
  onToggleCollapse: () => void;
  onNewChat: () => void;
  onLoadMoreConversations: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations,
  currentConversationId,
  collapsed,
  hasMoreConversations,
  isLoadingMoreConversations,
  onToggleCollapse,
  onNewChat,
  onLoadMoreConversations,
  onSelectConversation,
  onDeleteConversation,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const sentinel = loadMoreSentinelRef.current;

    if (!root || !sentinel || !hasMoreConversations || isLoadingMoreConversations) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          onLoadMoreConversations();
        }
      },
      {
        root,
        // Start loading early so users don't feel a "stop then fetch" jerk.
        rootMargin: '220px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreConversations, isLoadingMoreConversations, onLoadMoreConversations]);

  if (collapsed) {
    return (
      <div className="h-full w-12 shrink-0 border-r border-gray-200 bg-[#f7f7f8] flex flex-col items-center py-3 gap-3">
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded hover:bg-gray-200 text-gray-600"
          title="Expand sidebar"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={onNewChat}
          className="p-2 rounded hover:bg-blue-100 text-blue-600"
          title="New chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-64 shrink-0 border-r border-gray-200 bg-[#f7f7f8] flex flex-col">
      <div className="p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Chats</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewChat}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
            title="New chat"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
            title="Collapse sidebar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-400">No chats yet</div>
        ) : (
          <>
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group relative border-b border-gray-100 transition-colors ${
                  currentConversationId === conv.id ? 'bg-white border-l-2 border-l-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <button
                  onClick={() => onSelectConversation(conv.id)}
                  className="w-full text-left px-3 py-2.5 pr-10"
                >
                  <div className="text-sm font-medium text-gray-800 truncate pr-7">{conv.title}</div>
                  {conv.timestamp && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {conv.timestamp.toLocaleDateString()}
                    </div>
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteConversation(conv.id);
                  }}
                  className="absolute right-2 top-2.5 p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition"
                  title="Delete chat"
                  aria-label={`Delete chat ${conv.title}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0V5a1 1 0 011-1h4a1 1 0 011 1v2" />
                  </svg>
                </button>
              </div>
            ))}
            {isLoadingMoreConversations && (
              <div className="p-3 text-center text-xs text-gray-500">Loading more chats...</div>
            )}
            <div ref={loadMoreSentinelRef} className="h-1 w-full" aria-hidden="true" />
          </>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
